import { Button, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle, Slide, Tooltip, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import React, { useState } from 'react';
import { FormattedNumber } from 'react-intl';
import { toast } from 'react-toastify';
import { useCallbackObservable } from 'react-use-observable';
import { tap } from 'rxjs/operators';
import MyTable from 'v2/components/Shared/MyTable';
import MyTableBody from 'v2/components/Shared/MyTable/MyTableBody';
import MyTableCol from 'v2/components/Shared/MyTable/MyTableCol';
import MyTableHeader from 'v2/components/Shared/MyTable/MyTableHeader';
import MyTableRow from 'v2/components/Shared/MyTable/MyTableRow';
import PermissionHide from 'v2/components/Shared/PermissionHide';
import { dateMomentFormat } from 'v2/formatters/date';
import subscriptionsService from 'v2/services/subscriptions';

import type { ISubscriptionInvoice } from 'v2/interfaces/subscriptions';

const headers = [
  { title: '', ordering: false },
  { title: 'Selecione', ordering: false },
  { title: 'Fatura', ordering: false },
  { title: 'Vencimento', ordering: false },
  { title: 'Valor', ordering: false },
];

interface IProps {
  contractId: number;
  isOpen: boolean;
  invoices: ISubscriptionInvoice[];
  onOk: () => void;
  onCancel: () => void;
}

const useStyles = makeStyles((theme) => ({
  tableStatus: {
    position: 'absolute',
    left: 0,
    top: 0,
    height: '100%',
    width: '0.65rem',
    padding: '0 !important',
    zIndex: 10,
    borderRadius: 6,
    borderTopRightRadius: 0,
    borderBottomRightRadius: 0,
    ['&.paid' as any]: {
      backgroundColor: theme.palette.secondary.main,
    },
    ['&.refunded, &.waiting_refund' as any]: {
      backgroundColor: theme.palette.error.main,
    },
    ['&.canceled, &.duplicated, &.expired' as any]: {
      backgroundColor: theme.palette.primary.light,
    },
    ['&.open' as any]: {
      backgroundColor: '#2d86ca',
    },
    ['&.recovering, &.waiting_payment' as any]: {
      backgroundColor: '#fabb0a',
    },
    [theme.breakpoints.down('md')]: {
      width: '0.75rem',
      height: '0.75rem',
      top: '50%',
      left: '50%',
      borderRadius: 30,
      transform: 'translate3d(-50%, -50%, 0)',
    },
  },
  center: {
    textAlign: 'center',
  },
}));

const NegotiateModal = ({ contractId, isOpen, invoices, onOk, onCancel }: IProps) => {
  const classes = useStyles();

  const [invoicesSelected, setInvoicesSelected] = useState<number[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [handleSend] = useCallbackObservable(() => {
    setIsSubmitting(true);
    return subscriptionsService.sendNegotiateInvoices(contractId, invoicesSelected).pipe(
      tap(
        () => {
          onOk();
          toast.success(`${invoicesSelected.length} fatura(s) alteradas para negociadas com sucesso`);
          setIsSubmitting(false);
        },
        () => {
          setIsSubmitting(false);
          toast.error('Falha ao completar a solicitação');
        }
      )
    );
  }, [contractId, invoicesSelected]);

  const onSelect = (invoiceId: number) => {
    const exist = invoicesSelected.includes(invoiceId);

    if (exist) {
      setInvoicesSelected(invoicesSelected.filter((i) => i !== invoiceId));
      return;
    }

    setInvoicesSelected([...invoicesSelected, invoiceId]);
  };

  if (!isOpen) {
    return null;
  }

  return (
    <Dialog open={isOpen} TransitionComponent={Transition} onClose={onCancel} maxWidth='sm' fullWidth>
      <DialogTitle className={classes.center}>Escolha as faturas que deseja negociar</DialogTitle>
      <DialogContent>
        <MyTable responsive>
          <MyTableHeader index={headers} />
          <MyTableBody>
            {!invoices?.length && (
              <tr>
                <td colSpan={headers.length}>Nenhum registro encontrado.</td>
              </tr>
            )}

            {invoices?.length &&
              invoices
                .filter((i) => i.payment_status.id === 1 || i.payment_status.id === 11)
                .map((item, index: number) => (
                  <MyTableRow key={`invoices-row-${index}`}>
                    <MyTableCol status screenSizes={{ xs: 1, sm: 1, md: 6, lg: 3 }} mobileOrder={1}>
                      <Tooltip placement='top' title={item.payment_status.name}>
                        <div className={`${classes.tableStatus} ${item.payment_status.alias}`} />
                      </Tooltip>
                    </MyTableCol>
                    <MyTableCol screenSizes={{ xs: 12, sm: 2 }} mobileOrder={1}>
                      <Checkbox onClick={() => onSelect(item.id)} />
                    </MyTableCol>
                    <MyTableCol screenSizes={{ xs: 6, sm: 2, md: 6, lg: 3 }} mobileOrder={2}>
                      <div>{item.id}</div>
                    </MyTableCol>
                    <MyTableCol screenSizes={{ xs: 6, sm: 2, md: 6, lg: 3 }} mobileOrder={3}>
                      <div>{dateMomentFormat(item.expired_at)}</div>
                    </MyTableCol>
                    <MyTableCol screenSizes={{ xs: 12, sm: 2, md: 6, lg: 3 }} mobileOrder={4}>
                      <div className='ganho nowrap'>
                        <PermissionHide action='myeduzz:minhas-vendas:ocultar-valores' fallback={<em>oculto</em>}>
                          <FormattedNumber value={item.net_income} format={'BRL'} />
                        </PermissionHide>
                      </div>
                    </MyTableCol>
                  </MyTableRow>
                ))}
          </MyTableBody>
        </MyTable>

        <div className='m24'></div>
        <Typography variant='body2'>
          * As faturas ficarão com status de negociadas e o contrato irá considerá-las como se estivessem pagas.
        </Typography>
        <div className='m24'></div>
      </DialogContent>

      <DialogActions>
        <Button color='default' onClick={onCancel}>
          Cancelar
        </Button>
        <Button disabled={invoicesSelected?.length < 1 || isSubmitting} color='primary' variant='contained' onClick={handleSend}>
          Negociar ({invoicesSelected.length})
        </Button>
      </DialogActions>
    </Dialog>
  );
};
function Transition(props: any) {
  return <Slide direction='up' {...props} />;
}
export default NegotiateModal;
